import React from "react";
import { Link } from "react-router-dom";
import MarketingNav from "@/components/MarketingNav";
import QuoteCTA from "@/components/QuoteCTA";
import Seo from "@/components/Seo";
import SiteFooter from "@/components/SiteFooter";
import { BIZ } from "@/data/site";
import { services } from "@/data/services";
import { Image as ImageIcon, ArrowRight, Camera, Calculator } from "lucide-react";

// Placeholder gallery — real photo slots the owner can fill in later.
const placeholderJobs = services.slice(0, 8).map((s, i) => ({
  slug: s.slug,
  seoSlug: s.seoSlug,
  service: s.title,
  city: BIZ.serviceAreas[i % BIZ.serviceAreas.length],
  caption: `${s.title} in ${BIZ.serviceAreas[i % BIZ.serviceAreas.length]}, ${BIZ.stateShort}`,
}));

export default function GalleryPage() {
  return (
    <div className="bg-paper min-h-screen">
      <Seo
        title="Before & After Gallery"
        description={`Real before-and-after pressure washing and soft washing projects across ${BIZ.region}. House washing, roofs, driveways, decks, and more.`}
        path="/pressure-washing-gallery/"
      />
      <MarketingNav />
      <QuoteCTA />

      <section className="pt-6 pb-16">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-xs font-bold uppercase tracking-[0.25em] text-brand-700 mb-4 flex items-center gap-2">
            <Camera className="w-3.5 h-3.5" /> Gallery
          </div>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold tracking-tight text-stone-900 leading-[1.05]" style={{ fontFamily: "Outfit" }}>
            Before &amp; after,<br /><span className="text-electric-500 italic">on every truck.</span>
          </h1>
          <p className="mt-6 text-lg text-stone-600 max-w-2xl">
            Every visit ends with side-by-side photos. Below are recent projects from around {BIZ.region}. Owner-uploaded photos coming soon — see us on Google in the meantime.
          </p>
        </div>
      </section>

      <section className="pb-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="gallery-grid">
          {placeholderJobs.map((job, i) => (
            <div key={i} className="rounded-3xl bg-white border border-stone-200 overflow-hidden hover:shadow-lg transition-all">
              <div className="grid grid-cols-2 h-56">
                <div className="bg-gradient-to-br from-stone-200 to-stone-300 flex flex-col items-center justify-center text-stone-500">
                  <ImageIcon className="w-8 h-8" />
                  <div className="text-[10px] uppercase tracking-widest font-bold mt-2">Before</div>
                  <div className="text-[9px] mt-1 opacity-60">[owner photo]</div>
                </div>
                <div className="bg-gradient-to-br from-brand-100 to-brand-200 flex flex-col items-center justify-center text-brand-700">
                  <ImageIcon className="w-8 h-8" />
                  <div className="text-[10px] uppercase tracking-widest font-bold mt-2">After</div>
                  <div className="text-[9px] mt-1 opacity-70">[owner photo]</div>
                </div>
              </div>
              <div className="p-5">
                <div className="text-[10px] uppercase tracking-widest text-stone-500 font-bold">{job.service}</div>
                <div className="mt-1 font-bold text-stone-900" style={{ fontFamily: "Outfit" }}>{job.caption}</div>
                <Link
                  to={job.seoSlug ? `/${job.seoSlug}/` : `/services/${job.slug}`}
                  className="mt-3 inline-flex items-center gap-1 text-sm font-bold text-brand-700 hover:text-electric-500"
                >
                  Book this service <ArrowRight className="w-3.5 h-3.5" />
                </Link>
              </div>
            </div>
          ))}
        </div>
        <div className="max-w-7xl mx-auto px-6 md:px-12 mt-10 flex flex-wrap gap-3">
          <Link
            to="/pressure-washing-estimate-calculator/"
            className="inline-flex items-center gap-2 rounded-full bg-brand-600 hover:bg-electric-500 text-white font-bold px-6 py-3 shadow-lg transition-all hover:-translate-y-0.5"
          >
            <Calculator className="w-4 h-4" /> Get an instant estimate
          </Link>
        </div>
      </section>

      <SiteFooter />
    </div>
  );
}
